import React from "react";
import { Button } from "@truevault/aquilo";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/20/solid";
import { LightBulbIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";

export interface AlertCardProps {
  variant?: "info" | "success" | "warning" | "error" | "recommendation" | "general";
  size?: "default" | "compact";
  title?: string;
  description?: string | React.ReactNode;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonPosition?: "bottom" | "right";
  children?: React.ReactNode;
  icon?: React.ReactNode;
  showDefaultIcon?: boolean;
  containerClasses?: string;
}

const getVariantClasses = (variant: string) => {
  switch (variant) {
    case "info":
      return {
        container: "bg-blue-100",
        text: "text-blue-800",
        icon: "text-blue-600",
      };
    case "success":
      return {
        container: "bg-emerald-100",
        text: "text-emerald-800",
        icon: "text-emerald-600",
      };
    case "warning":
      return {
        container: "bg-amber-100",
        text: "text-amber-800",
        icon: "text-amber-700",
      };
    case "error":
      return {
        container: "bg-red-100",
        text: "text-red-800",
        icon: "text-red-600",
      };
    case "recommendation":
      return {
        container: "bg-primary-100",
        text: "text-primary-700",
        icon: "text-primary-600",
      };
    case "general":
      return {
        container: "bg-neutral-100",
        text: "text-neutral-800",
        icon: "text-neutral-500",
      };
    default:
      return {
        container: "bg-blue-100",
        text: "text-blue-800",
        icon: "text-blue-600",
      };
  }
};

const getDefaultIcon = (variant: string) => {
  switch (variant) {
    case "info":
      return <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "success":
      return <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "warning":
      return <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />;
    case "error":
      return <XCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "recommendation":
      return <LightBulbIcon className="w-5 h-5 flex-shrink-0" />;
    case "general":
      return <DocumentTextIcon className="w-5 h-5 flex-shrink-0" />;
    default:
      return <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />;
  }
};

const AlertCard = ({
  variant = "info",
  size = "default",
  title,
  description,
  buttonText,
  onButtonClick,
  buttonPosition = "bottom",
  children,
  icon,
  showDefaultIcon = true,
  containerClasses,
}: AlertCardProps) => {
  const variantClasses = getVariantClasses(variant);
  const displayIcon = icon || (showDefaultIcon ? getDefaultIcon(variant) : null);
  const isCompact = size === "compact";

  return (
    <div className="tw-boundary">
      <div
        className={clsx(
          "mt-6",
          "mb-6",
          "flex",
          isCompact ? "items-center" : buttonPosition === "right" ? "items-center" : "items-start",
          "text-sm",
          isCompact ? "gap-2 px-3 py-1.5" : "gap-3 p-4",
          "text-left",
          "rounded-md",
          variantClasses.container,
          containerClasses,
        )}
      >
        {displayIcon && <div className={variantClasses.icon}>{displayIcon}</div>}
        <div className="flex-1">
          {title && (
            <div
              className={clsx(
                "font-semibold",
                isCompact ? "mb-0 leading-5" : "mb-1",
                variantClasses.text,
              )}
            >
              {title}
            </div>
          )}
          {description && <p className={`m-0 leading-5 ${variantClasses.text}`}>{description}</p>}
          {children && <div className={variantClasses.text}>{children}</div>}
          {buttonText && onButtonClick && buttonPosition === "bottom" && (
            <div className="mt-3">
              <Button size="s" color="tertiary" onClick={onButtonClick}>
                {buttonText}
              </Button>
            </div>
          )}
        </div>
        {buttonText && onButtonClick && buttonPosition === "right" && (
          <div className="ml-3 flex-shrink-0">
            <Button size="s" color="tertiary" onClick={onButtonClick}>
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertCard;
